import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  ReactConsentContext,
  useSelectedWorkspace,
  useInfo,
} from '../../Flow/ConsentContextProvider';
import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { useFeatureGate } from '@atlassian/feature-gates-react';
import { OAuthConsentFeatureGate } from '../../FeatureGates/FeatureGates';
import {
  fetchWorkspacePolicyCheck,
  type PolicyCheckSuccess,
} from '../shared/agent-consent/domain/policyClient';
import { parseMcpPolicyData } from './domain/policyParse';
import type { McpPolicyIntentGroup } from './domain/policyTypes';
import type { TrelloWorkspace } from './domain/trelloTypes';
import {
  getInitialTrelloWorkspaceSelection,
  getSelectableTrelloWorkspaces,
  normalizeTrelloWorkspaceSelection,
} from './domain/trelloWorkspaceSelection';
import {
  classifyScopesWithOverride,
  type ScopeClassification,
} from './domain/trelloScopes';

export const MCP_POLICY_STATUS = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  error: 'error',
} as const;

export type McpPolicyStatus =
  (typeof MCP_POLICY_STATUS)[keyof typeof MCP_POLICY_STATUS];

/** Why policy check failed; only set when `mcpPolicyStatus === 'error'`. */
export const MCP_POLICY_ERROR_KIND = {
  policyFetchFailed: 'policyFetchFailed',
  redirectNotAllowed: 'redirectNotAllowed',
  missingRedirect: 'missingRedirect',
  unexpected: 'unexpected',
} as const;

export type McpPolicyErrorKind =
  (typeof MCP_POLICY_ERROR_KIND)[keyof typeof MCP_POLICY_ERROR_KIND];

export type McpContext = {
  isEditingPermissions: boolean;
  setIsEditingPermissions: (value: boolean) => void;
  mcpPolicyStatus: McpPolicyStatus;
  mcpPolicyErrorMessage?: string | null;
  mcpPolicyErrorKind?: McpPolicyErrorKind | null;
  mcpRedirectAllowed: boolean;
  /** Intent groups from the API, ordered read → write → search. */
  mcpByIntent: McpPolicyIntentGroup[];
  /** Selected toolset names (raw name from API, e.g. "read_jira"). */
  mcpSelectedToolsetNames: ReadonlySet<string>;
  toggleMcpToolset: (toolsetName: string, selected: boolean) => void;
  /** Scope classification: which product lines (Trello / Atlassian) are in the token. */
  scopeClassification: ScopeClassification;
  /** Currently selected Trello workspaces. Only contains multiple values when the multi-select gate is enabled. */
  selectedTrelloWorkspaces: TrelloWorkspace[];
  setSelectedTrelloWorkspaces: (workspaces: TrelloWorkspace[]) => void;
  /** Whether the Trello workspace selector allows more than one workspace. */
  isTrelloWorkspaceMultiSelectEnabled?: boolean;
  /** Mock Trello workspaces available for selection (prototype — will be replaced by AGG query). */
  availableTrelloWorkspaces: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (Relay suspend). */
  trelloWorkspacesLoading: boolean;
  /**
   * True when the token requested inbox/planner scopes but the selected Trello workspace
   * is outside the user's managing organization, so those scopes are not available.
   */
  inboxPlannerScopesDisabled: boolean;
};

export const ReactMcpContext = createContext<McpContext | null>(null);

export const useMcpContext = () => {
  const context = useContext(ReactMcpContext);
  if (!context) {
    throw new Error('useMcpContext must be used within McpContextProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Policy state — consolidated into a single reducer
// ---------------------------------------------------------------------------

type PolicyState =
  | { status: 'idle' | 'loading' }
  | {
      status: 'error';
      errorMessage: string | null;
      errorKind: McpPolicyErrorKind;
    }
  | {
      status: 'success';
      byIntent: McpPolicyIntentGroup[];
      crossOrgScopesRemoved: boolean;
    };

type PolicyAction =
  | { type: 'RESET' }
  | { type: 'LOADING' }
  | {
      type: 'SUCCESS';
      byIntent: McpPolicyIntentGroup[];
      crossOrgScopesRemoved: boolean;
    }
  | {
      type: 'ERROR';
      errorMessage: string | null;
      errorKind: McpPolicyErrorKind;
    };

function policyReducer(_: PolicyState, action: PolicyAction): PolicyState {
  switch (action.type) {
    case 'RESET':
      return { status: 'idle' };
    case 'LOADING':
      return { status: 'loading' };
    case 'SUCCESS':
      return {
        status: 'success',
        byIntent: action.byIntent,
        crossOrgScopesRemoved: action.crossOrgScopesRemoved,
      };
    case 'ERROR':
      return {
        status: 'error',
        errorMessage: action.errorMessage,
        errorKind: action.errorKind,
      };
  }
}

function mergeMcpPolicyIntents(
  intentGroups: McpPolicyIntentGroup[],
): McpPolicyIntentGroup[] {
  const merged = new Map<string, McpPolicyIntentGroup>();

  for (const group of intentGroups) {
    const existing = merged.get(group.id);
    if (existing) {
      const existingNames = new Set(
        existing.toolsets.map((toolset) => toolset.name),
      );
      const newToolsets = group.toolsets.filter((toolset) => {
        if (existingNames.has(toolset.name)) {
          return false;
        }
        existingNames.add(toolset.name);
        return true;
      });
      existing.toolsets = [...existing.toolsets, ...newToolsets];
    } else {
      merged.set(group.id, { ...group, toolsets: [...group.toolsets] });
    }
  }

  const intentOrder = ['read', 'write', 'search'];
  return intentOrder
    .filter((id) => merged.has(id))
    .map((id) => merged.get(id)!);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type McpContextProviderProps = PropsWithChildren<{
  /** Override the list of Trello workspaces (used in tests / Storybook). */
  trelloWorkspaces?: TrelloWorkspace[];
  /** Whether Trello workspaces are currently being fetched (e.g. via Relay suspend). */
  trelloWorkspacesLoading?: boolean;
}>;

export const McpContextProvider: FunctionComponent<McpContextProviderProps> = ({
  children,
  trelloWorkspaces,
  trelloWorkspacesLoading = false,
}) => {
  const consentContext = useContext(ReactConsentContext);
  if (!consentContext) {
    throw new Error(
      'McpContextProvider must be used within ConsentContextProvider',
    );
  }

  const { token } = consentContext;
  const info = useInfo();
  const analyticsClient = useAnalytics();
  const { selectedWorkspace } = useSelectedWorkspace();

  const [isEditingPermissions, setIsEditingPermissions] = useState(false);
  const [policyState, dispatchPolicy] = useReducer(policyReducer, {
    status: 'idle',
  });
  // Separate policy state for Trello workspace policy check
  const [trelloPolicyState, dispatchTrelloPolicy] = useReducer(policyReducer, {
    status: 'idle',
  });
  const [selectedNames, setSelectedNames] = useState<ReadonlySet<string>>(
    new Set(),
  );

  // Trello workspace state
  const [selectedTrelloWorkspaces, setSelectedTrelloWorkspacesState] = useState<
    TrelloWorkspace[]
  >([]);
  // Tracks whether we've already applied the initial workspace prefill, so a
  // user clearing their selection is not immediately overridden.
  const trelloPrefillAppliedRef = useRef(false);

  const trelloWorkspaceMultiSelectionEnabled = useFeatureGate(
    OAuthConsentFeatureGate.TRELLO_MCP_MULTI_WORKSPACE_SELECTION,
  );

  const trelloMixedWorkspaceEnabled = useFeatureGate(
    OAuthConsentFeatureGate.TRELLO_MCP_MIXED_WORKSPACE,
  );

  const scopeClassification = useMemo<ScopeClassification>(
    () =>
      classifyScopesWithOverride(
        token.scopes ?? [],
        info.hasTrelloProductAccess,
        trelloMixedWorkspaceEnabled,
      ),
    [token.scopes, info.hasTrelloProductAccess, trelloMixedWorkspaceEnabled],
  );

  const isTrelloWorkspaceMultiSelectEnabled =
    trelloWorkspaceMultiSelectionEnabled && !scopeClassification.hasAtlassian;

  const setSelectedTrelloWorkspaces = useCallback(
    (workspaces: TrelloWorkspace[]) => {
      setSelectedTrelloWorkspacesState(() =>
        normalizeTrelloWorkspaceSelection(
          workspaces,
          isTrelloWorkspaceMultiSelectEnabled,
        ),
      );
    },
    [isTrelloWorkspaceMultiSelectEnabled],
  );

  // Trello workspaces: use the override prop (tests / Storybook) or fall back
  // to an empty list. In production the parent tree will wrap with a Suspense
  // boundary and call `useTrelloWorkspaces()` from `domain/getTrelloWorkspaces`
  // to populate this via the `trelloWorkspaces` prop.
  const availableTrelloWorkspaces = useMemo<TrelloWorkspace[]>(() => {
    if (!scopeClassification.hasTrello) return [];
    return getSelectableTrelloWorkspaces({
      availableWorkspaces: trelloWorkspaces ?? [],
      selectedWorkspaces: selectedTrelloWorkspaces,
      isMultiSelectEnabled: isTrelloWorkspaceMultiSelectEnabled,
    });
  }, [
    scopeClassification.hasTrello,
    trelloWorkspaces,
    selectedTrelloWorkspaces,
    isTrelloWorkspaceMultiSelectEnabled,
  ]);

  // Merge site policy and Trello policy byIntent arrays.
  // Both are ordered read → write → search; merge toolsets within matching intents.
  const byIntent = useMemo<McpPolicyIntentGroup[]>(() => {
    const siteIntents =
      policyState.status === 'success' ? policyState.byIntent : [];
    const trelloIntents =
      trelloPolicyState.status === 'success' ? trelloPolicyState.byIntent : [];

    if (trelloIntents.length === 0) return siteIntents;
    if (siteIntents.length === 0) return trelloIntents;

    return mergeMcpPolicyIntents([...siteIntents, ...trelloIntents]);
  }, [policyState, trelloPolicyState]);

  const { setSelectedWorkspace } = consentContext;

  // Reset editing mode when workspace changes.
  // In mixed flows (both Atlassian + Trello), also reset Trello workspace.
  useEffect(() => {
    setIsEditingPermissions(false);
    if (scopeClassification.hasAtlassian) {
      setSelectedTrelloWorkspaces([]);
    }
  }, [
    selectedWorkspace,
    scopeClassification.hasAtlassian,
    setSelectedTrelloWorkspaces,
  ]);

  useEffect(() => {
    setSelectedTrelloWorkspacesState((current) =>
      normalizeTrelloWorkspaceSelection(
        current,
        isTrelloWorkspaceMultiSelectEnabled,
      ),
    );
  }, [isTrelloWorkspaceMultiSelectEnabled]);

  // Prefill the Trello workspace selection once the workspaces have loaded
  // (Trello-only flow). A single workspace auto-selects; multiple workspaces
  // require user selection. Mixed flows are driven by the Atlassian SiteSelector
  // and reset Trello on site change, so we skip prefill there. The ref guard
  // ensures we only prefill on initial load and never re-apply after the user
  // clears their selection.
  useEffect(() => {
    if (!scopeClassification.hasTrello || scopeClassification.hasAtlassian) {
      return;
    }
    if (trelloPrefillAppliedRef.current) {
      return;
    }
    if (selectedTrelloWorkspaces.length > 0) {
      trelloPrefillAppliedRef.current = true;
      return;
    }
    const initialSelection = getInitialTrelloWorkspaceSelection(
      availableTrelloWorkspaces,
    );
    if (initialSelection.length > 0) {
      trelloPrefillAppliedRef.current = true;
      setSelectedTrelloWorkspaces(initialSelection);
    }
  }, [
    scopeClassification,
    availableTrelloWorkspaces,
    selectedTrelloWorkspaces,
    setSelectedTrelloWorkspaces,
  ]);

  // In Trello-only flows the Atlassian SiteSelector is hidden, so we
  // drive the policy fetch from Trello workspace selection instead.
  // Selecting a Trello workspace auto-selects the (hidden) Atlassian workspace
  // to trigger the policy fetch; clearing it resets the policy.
  useEffect(() => {
    if (!scopeClassification.hasTrello || scopeClassification.hasAtlassian) {
      return; // Only applies to Trello-only flows
    }
    const firstWorkspace = token.workspaces?.[0] ?? null;
    if (
      selectedTrelloWorkspaces.length > 0 &&
      !selectedWorkspace &&
      firstWorkspace
    ) {
      setSelectedWorkspace?.(firstWorkspace);
    } else if (selectedTrelloWorkspaces.length === 0 && selectedWorkspace) {
      setSelectedWorkspace?.(null);
    }
  }, [
    scopeClassification,
    selectedTrelloWorkspaces,
    selectedWorkspace,
    token.workspaces,
    setSelectedWorkspace,
  ]);

  // Fetch policy whenever token or selectedWorkspace changes
  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (!selectedWorkspace) {
      // No workspace selected — either the user hasn't picked one yet,
      // there are none available, or the key is missing from the token.
      // Stay idle so we don't fire a policy fetch that would fail and
      // show a misleading error alongside the SiteSelector's empty-state.
      dispatchPolicy({ type: 'RESET' });
      setSelectedNames(new Set());
      return;
    }

    const callback = token.redirectUrl?.callback ?? '';
    if (!callback) {
      dispatchPolicy({
        type: 'ERROR',
        errorMessage: null,
        errorKind: MCP_POLICY_ERROR_KIND.missingRedirect,
      });
      setSelectedNames(new Set());
      analyticsClient.pushTrackEvent({
        actionSubject: 'mcpPolicyCheck',
        action: 'failed',
        source: 'oauthConsentScreen',
        attributes: { reason: MCP_POLICY_ERROR_KIND.missingRedirect },
      });
      return;
    }

    const ac = new AbortController();
    dispatchPolicy({ type: 'LOADING' });
    setSelectedNames(new Set());

    void (async () => {
      try {
        const result = await fetchWorkspacePolicyCheck(
          { contextJwt: token.raw, selectedWorkspace },
          ac.signal,
        );

        if (ac.signal.aborted) return;

        if (!result.ok) {
          dispatchPolicy({
            type: 'ERROR',
            errorMessage: result.errorMessage,
            errorKind: MCP_POLICY_ERROR_KIND.policyFetchFailed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.policyFetchFailed },
          });
          return;
        }

        if (!result.data.redirectAllowed) {
          dispatchPolicy({
            type: 'ERROR',
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.redirectNotAllowed },
          });
          return;
        }

        const mcpData = parseMcpPolicyData(result.data);
        const newByIntent = mcpData.byIntent;
        dispatchPolicy({
          type: 'SUCCESS',
          byIntent: newByIntent,
          crossOrgScopesRemoved: mcpData.crossOrgScopesRemoved,
        });
        // Pre-select all toolsets
        setSelectedNames(
          new Set(newByIntent.flatMap((g) => g.toolsets.map((t) => t.name))),
        );
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatchPolicy({
          type: 'ERROR',
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [token, selectedWorkspace, analyticsClient]);

  // Fetch Trello policy when one or more Trello workspaces are selected.
  // Uses the same /consent/workspace endpoint as Atlassian site policy,
  // passing the Trello workspace ARI as the selectedWorkspace parameter.
  useEffect(() => {
    if (token.serviceType !== 'MCP') return;
    if (!scopeClassification.hasTrello) return;

    if (selectedTrelloWorkspaces.length === 0) {
      dispatchTrelloPolicy({ type: 'RESET' });
      return;
    }

    const ac = new AbortController();
    dispatchTrelloPolicy({ type: 'LOADING' });

    void (async () => {
      try {
        const results = await Promise.all(
          selectedTrelloWorkspaces.map((workspace) =>
            fetchWorkspacePolicyCheck(
              {
                contextJwt: token.raw,
                selectedWorkspace: workspace.id,
              },
              ac.signal,
            ),
          ),
        );

        if (ac.signal.aborted) return;

        const failedResult = results.find((result) => !result.ok);
        if (failedResult && !failedResult.ok) {
          dispatchTrelloPolicy({
            type: 'ERROR',
            errorMessage: failedResult.errorMessage,
            errorKind: MCP_POLICY_ERROR_KIND.policyFetchFailed,
          });
          return;
        }

        const successfulResults = results.filter(
          (result): result is PolicyCheckSuccess => result.ok,
        );

        if (successfulResults.some((result) => !result.data.redirectAllowed)) {
          dispatchTrelloPolicy({
            type: 'ERROR',
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          return;
        }

        const parsedPolicyData = successfulResults.map((result) =>
          parseMcpPolicyData(result.data),
        );
        const newByIntent = mergeMcpPolicyIntents(
          parsedPolicyData.flatMap((data) => data.byIntent),
        );
        dispatchTrelloPolicy({
          type: 'SUCCESS',
          byIntent: newByIntent,
          crossOrgScopesRemoved: parsedPolicyData.some(
            (data) => data.crossOrgScopesRemoved,
          ),
        });
        // Pre-select all Trello toolsets, adding to existing selection
        setSelectedNames((prev) => {
          const next = new Set(prev);
          for (const g of newByIntent) {
            for (const t of g.toolsets) next.add(t.name);
          }
          return next;
        });
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatchTrelloPolicy({
          type: 'ERROR',
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [token, selectedTrelloWorkspaces, scopeClassification.hasTrello]);

  const toggleMcpToolset = useCallback(
    (toolsetName: string, selected: boolean) => {
      setSelectedNames((prev) => {
        const next = new Set(prev);
        if (selected) next.add(toolsetName);
        else next.delete(toolsetName);
        return next;
      });
    },
    [],
  );

  // Derive combined policy status from both site and Trello policy states.
  // In Trello-only flow, policyState stays 'idle' — use trelloPolicyState.
  // In mixed flow, both may be active — show error only if both have errors.
  const combinedPolicyStatus = useMemo<McpPolicyStatus>(() => {
    const site = policyState.status;
    const trello = trelloPolicyState.status;
    if (scopeClassification.hasTrello && !scopeClassification.hasAtlassian) {
      return trello;
    }
    // If either succeeded, consider it a success
    if (site === 'success' || trello === 'success') return 'success';
    // If either is loading, show loading
    if (site === 'loading' || trello === 'loading') return 'loading';
    // If either errored, show error
    if (site === 'error') return 'error';
    if (trello === 'error') return 'error';
    return 'idle';
  }, [policyState.status, scopeClassification, trelloPolicyState.status]);

  const combinedPolicyErrorMessage = useMemo(() => {
    if (
      scopeClassification.hasTrello &&
      !scopeClassification.hasAtlassian &&
      trelloPolicyState.status === 'error'
    ) {
      return trelloPolicyState.errorMessage;
    }
    if (policyState.status === 'error') return policyState.errorMessage;
    if (trelloPolicyState.status === 'error')
      return trelloPolicyState.errorMessage;
    return null;
  }, [policyState, scopeClassification, trelloPolicyState]);

  const combinedPolicyErrorKind = useMemo(() => {
    if (
      scopeClassification.hasTrello &&
      !scopeClassification.hasAtlassian &&
      trelloPolicyState.status === 'error'
    ) {
      return trelloPolicyState.errorKind;
    }
    if (policyState.status === 'error') return policyState.errorKind;
    if (trelloPolicyState.status === 'error')
      return trelloPolicyState.errorKind;
    return null;
  }, [policyState, scopeClassification, trelloPolicyState]);

  // Determine if cross-org scopes (inbox/planner) are disabled for the selected Trello workspace.
  // The policy response includes a crossOrgScopesRemoved flag when the selected workspace
  // is outside the user's managing organization.
  const inboxPlannerScopesDisabled = useMemo(() => {
    // Only relevant when Trello scopes are in play and a workspace is selected
    if (
      !scopeClassification.hasTrello ||
      selectedTrelloWorkspaces.length === 0
    ) {
      return false;
    }

    // Check the Trello policy response for crossOrgScopesRemoved flag
    if (trelloPolicyState.status === 'success') {
      return trelloPolicyState.crossOrgScopesRemoved;
    }

    return false;
  }, [
    scopeClassification.hasTrello,
    selectedTrelloWorkspaces,
    trelloPolicyState,
  ]);

  const value = useMemo<McpContext>(
    () => ({
      isEditingPermissions,
      setIsEditingPermissions,
      mcpPolicyStatus: combinedPolicyStatus,
      mcpPolicyErrorMessage: combinedPolicyErrorMessage,
      mcpPolicyErrorKind: combinedPolicyErrorKind,
      mcpRedirectAllowed: combinedPolicyStatus !== 'error',
      mcpByIntent: byIntent,
      mcpSelectedToolsetNames: selectedNames,
      toggleMcpToolset,
      scopeClassification,
      selectedTrelloWorkspaces,
      setSelectedTrelloWorkspaces,
      isTrelloWorkspaceMultiSelectEnabled,
      availableTrelloWorkspaces,
      trelloWorkspacesLoading,
      inboxPlannerScopesDisabled,
    }),
    [
      isEditingPermissions,
      combinedPolicyStatus,
      combinedPolicyErrorMessage,
      combinedPolicyErrorKind,
      byIntent,
      selectedNames,
      toggleMcpToolset,
      scopeClassification,
      selectedTrelloWorkspaces,
      setSelectedTrelloWorkspaces,
      isTrelloWorkspaceMultiSelectEnabled,
      availableTrelloWorkspaces,
      trelloWorkspacesLoading,
      inboxPlannerScopesDisabled,
    ],
  );

  return (
    <ReactMcpContext.Provider value={value}>
      {children}
    </ReactMcpContext.Provider>
  );
};

export default McpContextProvider;
